import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { motion } from 'framer-motion';
import { FaRuler, FaLightbulb, FaCalculator, FaChartLine, FaQuestionCircle, FaInfoCircle, FaCheckCircle } from 'react-icons/fa';

const WavelengthCalculator = () => {
  const [v, setV] = useState(''); // Speed
  const [f, setF] = useState(''); // Frequency
  const [λ, setLambda] = useState(''); // Wavelength
  const [showGraph, setShowGraph] = useState(false);

  const calculateWavelength = () => {
    if (!v || !f) {
      alert('Please enter both speed and frequency');
      return;
    }

    const vValue = parseFloat(v);
    const fValue = parseFloat(f);

    // Validate inputs
    if (isNaN(vValue) || isNaN(fValue)) {
      alert('Please enter valid numbers');
      return;
    }

    if (vValue <= 0 || fValue <= 0) {
      alert('Speed and frequency must be positive numbers');
      return;
    }

    // Calculate wavelength using λ = v/f
    const λValue = vValue / fValue;
    setLambda(λValue.toFixed(2));
    setShowGraph(true);
  };

  const generateGraphData = () => {
    if (!v || !f || !λ) return null;

    const vValue = parseFloat(v);
    const fValue = parseFloat(f);
    const λValue = parseFloat(λ);

    // Generate wave pattern graph data
    const series = [
      {
        name: 'Wave Pattern',
        data: Array.from({ length: 100 }, (_, i) => {
          const x = i * 0.1;
          const y = Math.sin(x * 2 * Math.PI / λValue) * 2;
          return {
            x,
            y
          };
        }),
      },
    ];

    const options = {
      chart: {
        type: 'line',
        height: 400,
        zoom: {
          enabled: false,
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
        }
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: 2,
        colors: ['#00E396'],
      },
      xaxis: {
        title: {
          text: 'Distance',
          style: {
            fontSize: '14px',
            fontWeight: 600,
            color: '#373d3f'
          }
        },
        labels: {
          style: {
            fontSize: '12px',
            fontWeight: 500,
            colors: '#78909c'
          },
          formatter: function (val) {
            return val.toFixed(1);
          }
        },
        axisBorder: {
          show: true,
          color: '#e0e0e0'
        },
      },
      yaxis: {
        title: {
          text: 'Amplitude',
          style: {
            fontSize: '14px',
            fontWeight: 600,
            color: '#373d3f'
          }
        },
        labels: {
          style: {
            fontSize: '12px',
            fontWeight: 500,
            colors: '#78909c'
          },
          formatter: function (val) {
            return val.toFixed(1);
          }
        },
        axisBorder: {
          show: true,
          color: '#e0e0e0'
        },
      },
      title: {
        text: 'Wave Pattern',
        align: 'center',
        style: {
          fontSize: '18px',
          fontWeight: 700,
          color: '#263238'
        }
      },
      markers: {
        size: 0,
      },
      grid: {
        borderColor: '#e0e0e0',
        strokeDashArray: 2,
        xaxis: {
          lines: {
            show: true
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        },
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val.toFixed(2);
          }
        },
        theme: 'dark'
      },
      annotations: {
        xaxis: [
          {
            x: λValue,
            borderColor: '#FF4560',
            label: {
              borderColor: '#FF4560',
              style: {
                color: '#fff',
                background: '#FF4560',
                fontSize: '12px',
                fontWeight: 600
              },
              text: 'Wavelength = ' + λValue.toFixed(2) + ' m'
            }
          }
        ]
      }
    };

    return { series, options };
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-green-50 to-blue-50">
      <Helmet>
        <title>Wavelength Calculator | Physics Tool</title>
        <meta name="description" content="Calculate wavelength using speed and frequency. Interactive calculator with wave pattern visualization showing wavelength characteristics." />
        <meta name="keywords" content="wavelength calculator, physics calculator, wave calculator, frequency calculator" />
        <meta property="og:title" content="Wavelength Calculator | Physics Tool" />
        <meta property="og:description" content="Calculate wavelength using speed and frequency. Interactive calculator with wave pattern visualization showing wavelength characteristics." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Wavelength Calculator | Physics Tool" />
        <meta name="twitter:description" content="Calculate wavelength using speed and frequency. Interactive calculator with wave pattern visualization showing wavelength characteristics." />
        <link rel="canonical" href="https://yourdomain.com/physics-calculators/wavelength" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-green-600 mb-4">
            <FaRuler />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Wavelength Calculator</h1>
          <p className="text-xl text-gray-600">Calculate wavelength using speed and frequency</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Calculator</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Speed (v)</label>
                <input
                  type="number"
                  value={v}
                  onChange={(e) => setV(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter speed (m/s)"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Frequency (f)</label>
                <input
                  type="number"
                  value={f}
                  onChange={(e) => setF(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter frequency (Hz)"
                />
              </div>
              <div>
                <button
                  onClick={calculateWavelength}
                  className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Calculate Wavelength
                </button>
              </div>

              {v && f && λ && (
                <div className="mt-4 p-4 bg-green-100 rounded">
                  <div className="flex items-center">
                    <FaCalculator className="text-2xl text-green-600 mr-2" />
                    <div>
                      <h3 className="text-lg font-bold text-green-800">Results</h3>
                      <div className="mt-2 space-y-2">
                        <p className="text-green-800">Speed = {v} m/s</p>
                        <p className="text-green-800">Frequency = {f} Hz</p>
                        <p className="text-green-800">Wavelength (λ) = {λ} m</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-md">
                    <div className="flex items-center mb-4">
                      <FaLightbulb className="text-yellow-500 mr-3" />
                      <h3 className="text-lg font-semibold">Solution Explanation</h3>
                    </div>
                    <p className="text-gray-600">
                      Using wavelength formula:
                      <br />
                      λ = v / f
                      <br />
                      <br />
                      Where:
                      <br />
                      v = Speed = {v} m/s
                      <br />
                      f = Frequency = {f} Hz
                      <br />
                      λ = Wavelength = {λ} m
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showGraph && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Wave Pattern</h2>
              <Chart
                options={generateGraphData().options}
                series={generateGraphData().series}
                type="line"
                height={400}
              />
              <p className="mt-4 text-gray-600 text-center">
                Wave pattern visualization showing wavelength
              </p>
            </div>
          )}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center mb-4">
            <FaQuestionCircle className="text-green-600 text-2xl mr-3" />
            <h2 className="text-2xl font-bold">How to Use</h2>
          </div>
          <ol className="list-decimal list-inside space-y-2">
            <li>Enter the speed of the wave (v)</li>
            <li>Enter the frequency of the wave (f)</li>
            <li>Click "Calculate Wavelength" to get the result</li>
            <li>View the wave pattern visualization</li>
          </ol>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center mb-4">
            <FaInfoCircle className="text-green-600 text-2xl mr-3" />
            <h2 className="text-2xl font-bold">Features & Benefits</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center mb-2">
                <FaCheckCircle className="text-green-500 mr-2" />
                <h3 className="text-xl font-semibold">Wavelength Calculation</h3>
              </div>
              <p>Calculate wavelength using speed and frequency</p>
            </div>
            <div>
              <div className="flex items-center mb-2">
                <FaCheckCircle className="text-green-500 mr-2" />
                <h3 className="text-xl font-semibold">Interactive Visualization</h3>
              </div>
              <p>Unique wave pattern visualization showing wavelength</p>
            </div>
            <div>
              <div className="flex items-center mb-2">
                <FaCheckCircle className="text-green-500 mr-2" />
                <h3 className="text-xl font-semibold">Educational Tool</h3>
              </div>
              <p>Ideal for physics and wave theory students</p>
            </div>
            <div>
              <div className="flex items-center mb-2">
                <FaCheckCircle className="text-green-500 mr-2" />
                <h3 className="text-xl font-semibold">Step-by-Step Solutions</h3>
              </div>
              <p>See how calculations are performed</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center mb-4">
            <FaQuestionCircle className="text-green-600 text-2xl mr-3" />
            <h2 className="text-2xl font-bold">FAQs</h2>
          </div>
          <div className="space-y-6">
            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What is Wavelength?</h3>
              <p className="text-gray-600">
                Wavelength is the distance between two consecutive points in a wave that are in phase.
              </p>
            </div>

            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What units should I use?</h3>
              <p className="text-gray-600">
                For accurate calculations:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Speed should be in meters per second (m/s)</li>
                  <li>Frequency should be in Hertz (Hz)</li>
                  <li>Wavelength will be in meters (m)</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">How does the wave pattern visualization work?</h3>
              <p className="text-gray-600">
                The wave pattern visualization shows:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>The wave pattern based on calculated wavelength</li>
                  <li>The wavelength marked on the graph</li>
                  <li>How different wavelengths affect wave shape</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What are some real-world applications?</h3>
              <p className="text-gray-600">
                Wavelength calculations are important in many areas:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Sound engineering and acoustics</li>
                  <li>Electromagnetic spectrum analysis</li>
                  <li>Optics and laser technology</li>
                  <li>Radio and communication systems</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">Why is this calculator useful?</h3>
              <p className="text-gray-600">
                This calculator helps:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Students understand wave properties</li>
                  <li>Engineers design wave-based systems</li>
                  <li>Teachers demonstrate physics concepts</li>
                  <li>Anyone working with waves and frequencies</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WavelengthCalculator;
